import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import {
  Card,
  CardBody,
  Col,
  Row,
  Form,
  Input,
  Label,
  Button,
  Spinner,
  ListGroup,
  ListGroupItem,
} from "reactstrap";
import { toast } from "react-toastify";
import { get, postJson, putJson } from "../../../hook/request";
import Loader from "../../../Components/Widgets/Loader/generic";
import { Btn, H3 } from "../../../AbstractElements";
import Editor from "../../../Components/Forms/EditorText";
import CompAbilitato from "../../../Components/Forms/RadioAbilitato";

type ProjectFormState = {
  nome: string;
  descrizione: string;
  amministrazione: string;
  data_attivazione: string;
  data_termine: string;       
  abilitato: 1 | 0;
  utenti: number[];
  clienti: number[];
};

type Utente = {
  ID: number;
  nome: string;
  cognome?: string;
  email?: string;
};

type Cliente = {
  ID: number;
  ragionesociale?: string | null; 
  nominativo: string;
  codice?: string | null;
};

type ProgettoDetailResponse = {
  status: string;
  data: {
    ID: number;
    nome: string;
    data_ins: string;
    creato_da: number;
    data_attivazione: string | null;
    data_chiusura: string | null;
    cancellato: number;
    abilitato: 0 | 1 | "0" | "1";
    descrizione: string | null;
    amministrazione: string | null;
  };
  clienti: Cliente[];
  utenti: Utente[];
};

export default function ProjectForm() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const isEdit = useMemo(() => slug && slug !== "new", [slug]);

  const [loading, setLoading] = useState<boolean>(!!isEdit);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState<ProjectFormState>({
    nome: "",
    descrizione: "",
    amministrazione: "",
    data_attivazione: "",
    data_termine: "",
    abilitato: 1,
    utenti: [],
    clienti: [],
  });

  // AUTOCOMPLETE UTENTI
  const [userQuery, setUserQuery] = useState("");
  const [userSuggestions, setUserSuggestions] = useState<Utente[]>([]);
  const [searchingUsers, setSearchingUsers] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState<Utente[]>([]);

  // AUTOCOMPLETE CLIENTI
  const [clientQuery, setClientQuery] = useState("");
  const [clientSuggestions, setClientSuggestions] = useState<Cliente[]>([]);
  const [searchingClients, setSearchingClients] = useState(false);
  const [selectedClients, setSelectedClients] = useState<Cliente[]>([]);

  /* ====== EDIT: carica dettagli progetto + utenti + clienti ====== */
  useEffect(() => {
    if (!isEdit) return;

    (async () => {
      try {
        setLoading(true);

        const res = await get<ProgettoDetailResponse>(
          `/progetti/detail?ID=${slug}`
        );

        const raw: any = (res as any)?.status ? res : (res as any)?.data ?? res;
        const progetto = raw.data.data ?? raw; 
        const utentiData: Utente[] = Array.isArray(raw.data.utenti)
          ? raw.data.utenti
          : [];
        const clientiData: Cliente[] = Array.isArray(raw.data.clienti)
          ? raw.data.clienti
          : [];


         console.log(raw); 

        setForm({
          nome: progetto.nome ?? "",
          descrizione: progetto.descrizione ?? "",
          amministrazione: progetto.amministrazione ?? "",
          abilitato:
            progetto.abilitato === 1 || progetto.abilitato === "1" ? 1 : 0,
          data_attivazione: progetto.data_attivazione
            ? String(progetto.data_attivazione).slice(0, 10)
            : "",
          // il backend la chiama data_chiusura, noi la mappiamo su data_termine
          data_termine: progetto.data_chiusura
            ? String(progetto.data_chiusura).slice(0, 10)
            : "",
          utenti: utentiData.map((u: any) => Number(u.ID ?? u)),
          clienti: clientiData.map((c: any) => Number(c.ID ?? c)),
        });

        setSelectedUsers(
          utentiData.map((u: any) => ({
            ID: Number(u.ID ?? u),
            nome: u.nome ?? "",
            cognome: u.cognome,
            email: u.email,
          }))
        );
        setSelectedClients(
          clientiData.map((c: any) => ({
            ID: Number(c.ID ?? c),
            ragionesociale: c.ragionesociale ?? null,
            nominativo: c.nominativo ?? "",
            codice: c.codice ?? null,
          }))
        );
      } catch (err) {
        console.error(err);
        toast.error("Errore nel caricamento dettagli progetto");
      } finally {
        setLoading(false);
      }
    })();
  }, [isEdit, slug]);

  /* ====== Gestione input base ====== */
  const onChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  /* ====== Editor descrizione ====== */
  const handleDescrizioneChange = (html: string) => {
    setForm((prev) => ({
      ...prev,
      descrizione: html,
    }));
  };


    /* ====== Editor descrizione ====== */
  const handleAmministrazioneChange = (html: string) => {
    setForm((prev) => ({
      ...prev,
      amministrazione: html,
    }));
  };

  /* ====== Autocomplete UTENTI ====== */
  const handleUserQueryChange = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = e.target.value;
    setUserQuery(value);
    if (value.length < 2) {
      setUserSuggestions([]);
      return;
    }

    try {
      setSearchingUsers(true);
      const res = await get<Utente[]>(
        `/users/search?temp=${encodeURIComponent(value)}`
      );
      const list = (res as any)?.data ?? res;
      setUserSuggestions(Array.isArray(list) ? list : []);
    } catch {
      toast.error("Errore nella ricerca utenti");
    } finally {
      setSearchingUsers(false);
    }
  };

  const handleAddUser = (u: Utente) => {
    setForm((prev) => ({
      ...prev,
      utenti: prev.utenti.includes(u.ID)
        ? prev.utenti
        : [...prev.utenti, u.ID],
    }));

    setSelectedUsers((prev) =>
      prev.some((x) => x.ID === u.ID) ? prev : [...prev, u]
    );

    setUserQuery("");
    setUserSuggestions([]);
  };

  const handleRemoveUser = (id: number) => {
    setForm((prev) => ({
      ...prev,
      utenti: prev.utenti.filter((x) => x !== id),
    }));
    setSelectedUsers((prev) => prev.filter((u) => u.ID !== id));
  };

  /* ====== Autocomplete CLIENTI ====== */
  const handleClientQueryChange = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = e.target.value;
    setClientQuery(value);

    if (value.length < 2) {
      setClientSuggestions([]);
      return;
    }

    try {
      setSearchingClients(true);
      const res = await get<Cliente[]>(
        `/clienti/searchRagioneSociale?temp=${encodeURIComponent(value)}`
      );
      const list = (res as any)?.data ?? res;
      setClientSuggestions(Array.isArray(list) ? list : []);
    } catch {
      toast.error("Errore nella ricerca clienti");
    } finally {
      setSearchingClients(false);
    }
  };

  const handleAddClient = (c: Cliente) => {
    setForm((prev) => ({
      ...prev,
      clienti: prev.clienti.includes(c.ID)
        ? prev.clienti
        : [...prev.clienti, c.ID],
    }));

    setSelectedClients((prev) =>
      prev.some((x) => x.ID === c.ID) ? prev : [...prev, c]
    );

    setClientQuery("");
    setClientSuggestions([]);
  };

  const handleRemoveClient = (id: number) => {
    setForm((prev) => ({
      ...prev,
      clienti: prev.clienti.filter((x) => x !== id),
    }));
    setSelectedClients((prev) => prev.filter((c) => c.ID !== id));
  };

  /* ====== Submit ====== */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const user_email = localStorage.getItem("email") || "123";
    const fd = new FormData();
    const id = String(slug ?? "");

    fd.append("nome", form.nome);
    fd.append("descrizione", form.descrizione);
    fd.append("amministrazione", form.amministrazione);
    fd.append("data_attivazione", form.data_attivazione || "");
    // il backend ha "data_chiusura", ma se lato server accetti "data_termine" tienila così.
    // Se serve "data_chiusura", cambia il nome qui:
    fd.append("data_termine", form.data_termine || "");
    fd.append("abilitato", String(form.abilitato));
    fd.append("user_email", user_email);

    fd.append("utenti", form.utenti.join(","));
    fd.append("clienti", form.clienti.join(","));

    try {
      setSaving(true);
      if (isEdit) {
        fd.append("id", id);
        await putJson(`/progetti/edit?ID=${slug}`, fd);
        toast.success("Progetto aggiornato!");
      } else {
        await postJson("/progetti/insert", fd);
        toast.success("Progetto creato con successo!");
        window.location.reload();
        setForm({
          nome: "",
          descrizione: "",
          amministrazione: "",
          data_attivazione: "",
          data_termine: "",
          abilitato: 1,
          utenti: [],
          clienti: [],
        });
        setSelectedUsers([]);
        setSelectedClients([]);
        setUserQuery("");
        setClientQuery("");
      }
    } catch (err: any) {
      console.error(err);
      toast.error(
        err?.response?.data?.message ?? "Errore durante il salvataggio"
      );
    } finally {
      setSaving(false);
    }
  };

  /* ====== UI ====== */
  if (loading) return <Loader />;

  return (
    <div className="page-body pt-4">
      <Col xl={12}>
        <Card>
          <CardBody>
            <Col
              xl={12}
              className="mb-lg-3 d-flex justify-content-between"
            >
              <H3 className="mb-4">
                {isEdit ? "Aggiorna Progetto" : "Nuovo Progetto"}
              </H3>
              <Link to="/gestione-progetti/progetti">
                <Btn className="px-4" color="primary" type="button">
                  Torna Indietro
                </Btn>
              </Link>
            </Col>

            <Form onSubmit={handleSubmit}>
              <Row className="g-3">
                <Col md={12}>
                  <Label>Nome</Label>
                  <Input
                    name="nome"
                    value={form.nome}
                    onChange={onChange}
                  />
                </Col>

                <Col xs={12}>
                  <Label>Descrizione</Label>
                  <Editor
                    key={"it"}
                    value={form.descrizione}
                    onChange={handleDescrizioneChange}
                    height={360}
                  />
                </Col>

                                <Col xs={12}>
                  <Label>Amministrazione</Label>
                  <Editor
                    key={"it"}
                    value={form.amministrazione}
                    onChange={handleAmministrazioneChange}
                    height={360}
                  />
                </Col>

                <Col md={6}>
                  <Label>Data attivazione</Label>
                  <Input
                    type="date"
                    name="data_attivazione"
                    value={form.data_attivazione}
                    onChange={onChange}
                  />
                </Col>

                <Col md={6}>
                  <Label>Data termine</Label>
                  <Input
                    type="date"
                    name="data_termine"
                    value={form.data_termine}
                    onChange={onChange}
                  />
                </Col>

                {/* AUTOCOMPLETE UTENTI */}
                <Col md={12}>
                  <Label>Utenti</Label>
                  <Input
                    type="text"
                    placeholder="Cerca utente..."
                    value={userQuery}
                    onChange={handleUserQueryChange}
                  />
                  {searchingUsers && (
                    <div className="mt-1">
                      <Spinner size="sm" />
                    </div>
                  )}
                  {userSuggestions.length > 0 && (
                    <ListGroup className="mt-1">
                      {userSuggestions.map((u) => (
                        <ListGroupItem
                          key={u.ID}
                          action
                          onClick={() => handleAddUser(u)}
                        >
                          {u.nome} {u.cognome}{" "}
                          {u.email && (
                            <span className="text-muted">({u.email})</span>
                          )}
                        </ListGroupItem>
                      ))}
                    </ListGroup>
                  )}

                  <div className="mt-2 d-flex flex-wrap gap-2">
                    {selectedUsers.map((u) => (
                      <span
                        key={u.ID}
                        className="badge bg-primary d-flex align-items-center"
                      >
                        <span>
                          {u.nome} {u.cognome ?? ""}
                        </span>
                        <button
                          type="button"
                          className="btn-close btn-close-white btn-sm ms-2"
                          aria-label="Remove"
                          onClick={() => handleRemoveUser(u.ID)}
                        />
                      </span>
                    ))}
                  </div>
                </Col>

                {/* AUTOCOMPLETE CLIENTI */}
                <Col md={12}>
                  <Label>Clienti</Label>
                  <Input
                    type="text"
                    placeholder="Cerca cliente..."
                    value={clientQuery}
                    onChange={handleClientQueryChange}
                  />
                  {searchingClients && (
                    <div className="mt-1">
                      <Spinner size="sm" />
                    </div>
                  )}
                  {clientSuggestions.length > 0 && (
                    <ListGroup className="mt-1">
                      {clientSuggestions.map((c) => (
                        <ListGroupItem
                          key={c.ID}
                          action
                          onClick={() => handleAddClient(c)}
                        >
                          {c.ragionesociale ||
                            c.nominativo ||
                            `Cliente #${c.ID}`}{" "}
                          {c.codice && (
                            <span className="text-muted">({c.codice})</span>
                          )}
                        </ListGroupItem>
                      ))}
                    </ListGroup>
                  )}

                  <div className="mt-2 d-flex flex-wrap gap-2">
                    {selectedClients.map((c) => (
                      <span
                        key={c.ID}
                        className="badge bg-success d-flex align-items-center"
                      >
                        <span>
                          {c.ragionesociale ||
                            c.nominativo ||
                            `Cliente #${c.ID}`}
                        </span>
                        <button
                          type="button"
                          className="btn-close btn-close-white btn-sm ms-2"
                          aria-label="Remove"
                          onClick={() => handleRemoveClient(c.ID)}
                        />
                      </span>
                    ))}
                  </div>
                </Col>

                <Col xs={6}>
                  <CompAbilitato
                    value={form.abilitato}
                    onChange={(v) =>
                      setForm((p) => ({ ...p, abilitato: v }))
                    }
                  />
                </Col>
              </Row>

              <div className="d-flex justify-content-start mt-4">
                <Button color="primary" type="submit" disabled={saving}>
                  {saving ? (
                    <Spinner size="sm" />
                  ) : isEdit ? (
                    "Aggiorna"
                  ) : (
                    "Crea"
                  )}
                </Button>
              </div>
            </Form>
          </CardBody>
        </Card>
      </Col>
    </div>
  );
}
